import React, { useEffect, useState, useMemo } from "react";
import { axiosSalary, axiosMaster } from "../../api/axiosInstance";
import { FaFileExcel, FaSearch } from "react-icons/fa";
import { useAuth } from "../../auth/AuthContext";

const DailyReportByRestaurant = () => {

  /* ================= AUTH ================= */
  const { user } = useAuth();
  const groupId = user?.groupId;
  const restaurantId = user?.restaurantId;

  /* ================= STATE ================= */
  const [date, setDate] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("ALL");

  /* ================= FETCH ================= */
  const fetchReport = async () => {
    if (!date || !groupId || !restaurantId) return;

    try {
      setLoading(true);

      const [attendanceRes, empRes] = await Promise.all([
        axiosSalary.get(
          `/DailyAttendanceReport/DailyReportByRestaurantAndDate?groupId=${groupId}&restId=${restaurantId}&attendanceDate=${date}`
        ),
        axiosMaster.get(
          `/EmployeeMaster/GetByGroup?groupId=${groupId}`
        )
      ]);

      const attendance = attendanceRes.data || [];
      let employees = empRes.data || [];

      // ✅ Filter employees by restaurant
      employees = employees.filter(emp => emp.restId === restaurantId);

      const merged = mergeAttendanceWithEmployees(attendance, employees);

      setData(merged);

    } catch (err) {
      console.error("Report error:", err);
    } finally {
      setLoading(false);
    }
  };

  /* ================= MERGE ================= */
  const mergeAttendanceWithEmployees = (attendance, employees) => {

    const map = new Map();
    attendance.forEach(a => map.set(a.empId, a));

    return employees.map(emp => {

      const att = map.get(emp.empId);

      if (att) {
        return {
          ...att,
          empName: att.empName || emp.empName
        };
      }

      // ❌ ABSENT ENTRY
      return {
        empId: emp.empId,
        empName: emp.empName,
        attendanceDateDisplay: date,

        firstPunchDisplay: null,
        lastPunchDisplay: null,
        totalPunches: 0,
        workingMinutes: 0,
        lateMinutes: 0,
        earlyLeaveMinutes: 0,
        overtimeMinutes: 0,

        isPresent: false,
        isAbsent: true,
        isLate: false,

        remarks: "Absent"
      };
    });
  };

  /* ================= DEFAULT DATE ================= */
  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    setDate(today);
  }, []);

  useEffect(() => {
    fetchReport();
  }, [date, groupId, restaurantId]);

  /* ================= FILTER + SEARCH ================= */
  const filteredData = useMemo(() => {

    let filtered = data;

    // 🔍 SEARCH
    if (search) {
      const term = search.toLowerCase();
      filtered = filtered.filter(d =>
        d.empName?.toLowerCase().includes(term) ||
        String(d.empId).includes(term)
      );
    }

    // 🎯 SUMMARY FILTER
    if (activeFilter === "PRESENT") {
      filtered = filtered.filter(d => d.isPresent && !d.isAbsent);
    }
    else if (activeFilter === "ABSENT") {
      filtered = filtered.filter(d => d.isAbsent);
    }
    else if (activeFilter === "LATE") {
      filtered = filtered.filter(d => d.isLate && !d.isAbsent);
    }

    return filtered;

  }, [data, search, activeFilter]);

  /* ================= SUMMARY ================= */
  const summary = useMemo(() => {
    return {
      total: data.length,
      present: data.filter(d => d.isPresent).length,
      absent: data.filter(d => d.isAbsent).length,
      late: data.filter(d => d.isLate).length,
    };
  }, [data]);

  /* ================= EXPORT ================= */
  const exportExcel = () => {
    if (!filteredData.length) return;

    const rows = filteredData.map((r) => ({
      EmpId: r.empId,
      Name: `"${r.empName || ""}"`,
      Date: r.attendanceDateDisplay,
      FirstPunch: r.firstPunchDisplay || "",
      LastPunch: r.lastPunchDisplay || "",
      Punches: r.totalPunches,
      WorkingMinutes: r.workingMinutes,
      LateMinutes: r.lateMinutes,
      EarlyLeaveMinutes: r.earlyLeaveMinutes,
      OvertimeMinutes: r.overtimeMinutes,
      Status: r.isAbsent
        ? "Absent"
        : r.isLate
          ? "Late"
          : "Present",
      Remarks: `"${r.remarks || ""}"`,
    }));

    const csv =
      "data:text/csv;charset=utf-8," +
      [
        Object.keys(rows[0]).join(","),
        ...rows.map((e) => Object.values(e).join(",")),
      ].join("\n");

    const link = document.createElement("a");
    link.href = encodeURI(csv);
    link.download = `Restaurant_${restaurantId}_${date}.csv`;
    link.click();
  };

  return (
    <div className="bg-white p-4 md:p-5 rounded-xl shadow border">

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">

        {/* LEFT: TITLE */}
        <h2 className="text-lg font-semibold whitespace-nowrap">
          Daily Report (Restaurant)
        </h2>

        {/* RIGHT: SEARCH + DATE + EXPORT */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full md:w-auto">

          {/* SEARCH */}
          <div className="relative w-full sm:w-56">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search employee..."
              className="w-full pl-8 pr-2 py-1.5 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* DATE */}
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="border px-2 py-1.5 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />

          {/* EXPORT */}
          <button
            onClick={exportExcel}
            className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-md text-sm flex items-center justify-center gap-2 shadow-sm"
          >
            <FaFileExcel /> Export
          </button>

        </div>

      </div>

      {/* ================= SUMMARY (CLICKABLE) ================= */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 text-sm">

        <div onClick={() => setActiveFilter("ALL")}
          className={`bg-gray-100 p-2 rounded text-center cursor-pointer ${activeFilter === "ALL" && "ring-2 ring-gray-400"}`}>
          <p>Total</p>
          <p className="font-semibold">{summary.total}</p>
        </div>

        <div onClick={() => setActiveFilter("PRESENT")}
          className={`bg-green-100 p-2 rounded text-center cursor-pointer ${activeFilter === "PRESENT" && "ring-2 ring-green-500"}`}>
          <p className="text-green-700">Present</p>
          <p className="font-semibold">{summary.present}</p>
        </div>

        <div onClick={() => setActiveFilter("ABSENT")}
          className={`bg-red-100 p-2 rounded text-center cursor-pointer ${activeFilter === "ABSENT" && "ring-2 ring-red-500"}`}>
          <p className="text-red-700">Absent</p>
          <p className="font-semibold">{summary.absent}</p>
        </div>

        <div onClick={() => setActiveFilter("LATE")}
          className={`bg-yellow-100 p-2 rounded text-center cursor-pointer ${activeFilter === "LATE" && "ring-2 ring-yellow-500"}`}>
          <p className="text-yellow-700">Late</p>
          <p className="font-semibold">{summary.late}</p>
        </div>

      </div>

      {/* ================= TABLE ================= */}
      <div className="hidden md:block border rounded-lg max-h-[500px] overflow-auto">

        <table className="w-full text-xs border-collapse">

          <thead className="bg-gray-200 sticky top-0">
            <tr>
              <th className="border p-2">ID</th>
              <th className="border p-2 text-left">Name</th>
              <th className="border p-2">Date</th>
              <th className="border p-2">First</th>
              <th className="border p-2">Last</th>
              <th className="border p-2">Punch</th>
              <th className="border p-2">Work</th>
              <th className="border p-2">Late</th>
              <th className="border p-2">Early</th>
              <th className="border p-2">OT</th>
              <th className="border p-2">Status</th>
              <th className="border p-2">Remarks</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr><td colSpan="12" className="text-center p-4">Loading...</td></tr>
            ) : filteredData.length === 0 ? (
              <tr><td colSpan="12" className="text-center p-4">No data</td></tr>
            ) : filteredData.map(row => {

              const rowColor = row.isAbsent
                ? "bg-red-50"
                : row.isLate
                  ? "bg-yellow-50"
                  : "";

              return (
                <tr key={row.empId} className={`${rowColor} hover:bg-blue-50`}>
                  <td className="border p-2 text-center">{row.empId}</td>
                  <td className="border p-2">{row.empName}</td>
                  <td className="border p-2 text-center">{row.attendanceDateDisplay}</td>
                  <td className="border p-2">{row.firstPunchDisplay || "-"}</td>
                  <td className="border p-2">{row.lastPunchDisplay || "-"}</td>
                  <td className="border p-2 text-center">{row.totalPunches}</td>
                  <td className="border p-2 text-center">{row.workingMinutes}</td>
                  <td className="border p-2 text-center">{row.lateMinutes}</td>
                  <td className="border p-2 text-center">{row.earlyLeaveMinutes}</td>
                  <td className="border p-2 text-center">{row.overtimeMinutes}</td>
                  <td className="border p-2 text-center">
                    {row.isAbsent ? "Absent" : row.isLate ? "Late" : "Present"}
                  </td>
                  <td className="border p-2 text-xs">{row.remarks}</td>
                </tr>
              );
            })}
          </tbody>

        </table>
      </div>

    </div>
  );
};

export default DailyReportByRestaurant;